// Eskom PARCC — the global Audit Trail screen (the findability fix for Lindiwe's
// "query an audit trail for changes made on every transaction — who, what, where
// and when"). One filterable, exportable feed of every recorded change.
//
// Role-scoped by the same boundary as the register: cross-division roles see the
// whole window; a division-bound grant is served only transaction-scoped rows it
// can see (server-side, via useAuditFeed → the /api/scoped/audit-feed proxy) plus
// rows it authored — so this screen is not a second, looser boundary.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DataTable,
  LoadingState,
  ErrorState,
  EmptyState,
  FilterBar,
  SelectFilter,
  SearchInput,
  Button,
  Banner,
  usePagination,
  Pager,
  type Column,
} from '../../components/ui';
import { useAuditFeed } from '../../hooks';
import type { AuditLogEntry } from '../../lib/db';
import { actionLabel, surfaceLabel } from './auditActions';
import { whatChanged, buildAuditTrailSheet } from '../exports/buildAuditTrailSheet';
import { workbookFromSheet } from '../exports/workbook';
import { downloadWorkbook } from '../exports/download';

/** entityTypes whose entityId is a transaction reference (deep-linkable). */
const TX_TYPES = new Set(['Transaction', 'WorkingPaper', 'Finding']);

/** An ISO yyyy-mm-dd `offsetDays` from today (UTC), for the native date inputs. */
function isoDay(offsetDays: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function formatAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-ZA');
}

export function AuditTrailPage(): React.JSX.Element {
  const [from, setFrom] = useState(() => isoDay(-90));
  const [to, setTo] = useState(() => isoDay(0));
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  // The date range drives the server fetch WINDOW (two required bounds).
  const feed = useAuditFeed({ from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` });
  const all = useMemo(() => feed.data?.entries ?? [], [feed.data]);
  const truncated = feed.data?.truncated ?? false;

  const fromMs = Date.parse(`${from}T00:00:00.000Z`);
  const toMs = Date.parse(`${to}T23:59:59.999Z`);

  const actionOptions = useMemo(() => {
    const present = Array.from(new Set(all.map((r) => r.action))).sort((a, b) =>
      actionLabel(a).localeCompare(actionLabel(b)),
    );
    return [{ value: '', label: 'All actions' }, ...present.map((a) => ({ value: a, label: actionLabel(a) }))];
  }, [all]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter((r) => {
      // Belt-and-braces client date filter (the fetch window may be slightly wider).
      const t = Date.parse(r.at);
      if (!Number.isNaN(t) && (t < fromMs || t > toMs)) return false;
      if (actionFilter && r.action !== actionFilter) return false;
      if (!term) return true;
      return [r.actorEmail, r.entityId, actionLabel(r.action), surfaceLabel(r.action), whatChanged(r)].some(
        (v) => (v ?? '').toLowerCase().includes(term),
      );
    });
  }, [all, search, actionFilter, fromMs, toMs]);

  // 500 rows/page — an audit reviewer scans long runs, so the small theme default
  // (~12) is a click-tax. The window is already capped at 5000, so this is ≤10 pages.
  const pagination = usePagination(rows, 500);

  const columns = useMemo<Column<AuditLogEntry>[]>(
    () => [
      {
        key: 'at',
        header: 'When',
        sortable: true,
        accessor: (r) => r.at,
        render: (r) => <span className="audit-nowrap">{formatAt(r.at)}</span>,
      },
      { key: 'by', header: 'By', sortable: true, accessor: (r) => r.actorEmail ?? '', render: (r) => r.actorEmail ?? '—' },
      {
        key: 'action',
        header: 'Action',
        sortable: true,
        accessor: (r) => actionLabel(r.action),
        render: (r) => actionLabel(r.action),
      },
      {
        key: 'surface',
        header: 'Surface',
        sortable: true,
        accessor: (r) => surfaceLabel(r.action),
        render: (r) => <span className="anchor audit-nowrap">{surfaceLabel(r.action)}</span>,
      },
      {
        key: 'ref',
        header: 'Reference',
        sortable: true,
        accessor: (r) => r.entityId ?? '',
        render: (r) => {
          const id = r.entityId ?? '';
          if (id && r.entityType && TX_TYPES.has(r.entityType)) {
            return <Link to={`/transactions?ref=${encodeURIComponent(id)}`}>{id}</Link>;
          }
          return id || '—';
        },
      },
      { key: 'what', header: 'What changed', render: (r) => whatChanged(r) || '—' },
    ],
    [],
  );

  function onExport(): void {
    const wb = workbookFromSheet('Audit Trail', buildAuditTrailSheet(rows));
    downloadWorkbook(`audit-trail-${from}_to_${to}.xlsx`, wb);
  }

  if (feed.isLoading) return <LoadingState message="Loading the audit trail…" />;
  if (feed.error) {
    return (
      <ErrorState
        message="Could not load the audit trail."
        detail={feed.error instanceof Error ? feed.error.message : String(feed.error)}
        onRetry={() => void feed.refetch()}
      />
    );
  }

  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h3>Audit Trail</h3>
          <div className="small">{rows.length.toLocaleString('en-ZA')} event(s)</div>
        </div>
        <div className="actions">
          <Button onClick={onExport} disabled={rows.length === 0}>
            Export
          </Button>
        </div>
      </div>

      {truncated && (
        <Banner tone="info">
          Showing the {all.length.toLocaleString('en-ZA')} most recent changes in this date range
          — there are older ones as well. Narrow the From / To dates to load everything within a
          shorter span.
        </Banner>
      )}

      <FilterBar>
        <SearchInput label="Search" value={search} onChange={setSearch} placeholder="Reference, actor, detail…" />
        <SelectFilter label="Action" value={actionFilter} options={actionOptions} onChange={setActionFilter} />
        <label>
          From
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
      </FilterBar>

      {rows.length === 0 ? (
        <EmptyState message="No activity in this window." />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={pagination.slice}
            getRowKey={(r) => r.id}
            initialSort={{ key: 'at', dir: 'desc' }}
          />
          <Pager pagination={pagination} noun="event" />
        </>
      )}
    </div>
  );
}
